import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { Link, useLocation } from "react-router";
import { AppFooter } from "@/app/layout/app-footer";
import { PublicHeader } from "@/app/layout/public-header";
import { secretFieldRef } from "@/features/first-credential/secret-field-ref";
import { useAuth } from "@/core/auth/auth-provider";
import { getDefaultReturnTo } from "@/core/auth/return-to";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { SupportReference } from "@/shared/components/support-reference";
import { Alert, AlertDescription, AlertTitle, Button, Input, Label } from "@/shared/components/ui";
import {
  CredentialRecoveryApiError,
  isCredentialRecoveryReference,
  type CredentialRecoveryFinalizeResult,
} from "./credential-recovery-client";
import { parseInvitationProofFragment } from "./invitation-proof-fragment";
import {
  RegistrationFinancialOutcome,
  type FinancialHandoffDescriptor,
} from "./registration-financial-outcome";
import {
  acceptCompletionFinancialHandoff,
  clearRegistrationFinancialTokenByOwner,
  type RegistrationFinancialTokenOwner,
} from "./registration-financial-token-holder";
import { useCredentialRecoveryClient } from "./use-credential-recovery";

/**
 * 011-15 UI contract "Credential recovery journey (A §8.1)", steps 3-6, and
 * 011-21 addendum "Member completion and re-entry (FR-050)".
 *
 * `/register/invitation/recovery?ref=<recoveryReference>` is the post-login
 * return target of the recovery entry rendered by the already-completed
 * invitation surface. On mount the reference is read once into memory and
 * scrubbed from the URL BEFORE any request; the session is refreshed; and the
 * member is asked to paste their original invitation link so the proof (which
 * is never persisted anywhere in the browser) can be re-derived and finalized
 * with a Bearer token exactly once per Continue.
 */

export const RECOVERY_PATH = "/register/invitation/recovery";

const INVITATION_PATH = "/register/invitation";

const NO_REFERENCE_COPY = "Open the link from your invitation email to continue.";
const PASTE_LABEL = "Paste the link from your invitation email";
const PASTE_HELP =
  "Open the invitation email and paste its link here. Nothing is sent until you continue.";
const PASTE_ERROR =
  "That doesn't look like your invitation link. Open the email and copy the whole link.";
const SUPPORT_PATH = "/home/contactus";

/** 011-15 UI contract L185: the single generic guidance (with support entry) for 422/429/503. */
function GenericGuidance(): ReactElement {
  return (
    <>
      Open your invitation link again to try once more, or{" "}
      <Link to={SUPPORT_PATH} className="underline">
        contact support
      </Link>
      .
    </>
  );
}

/** 011-15 UI contract L196: copy-the-link-text fallback with a support entry. */
function RewrittenLinkFallback(): ReactElement {
  return (
    <>
      If your email program changed the link, open the email and copy the link text exactly as
      shown, or{" "}
      <Link to={SUPPORT_PATH} className="underline">
        contact support
      </Link>
      .
    </>
  );
}

type Phase =
  | { kind: "refreshing" }
  | { kind: "no-reference" }
  | { kind: "paste" }
  | {
      kind: "completed";
      correlationId: string;
      handoff: FinancialHandoffDescriptor;
      /** True when the handoff deposited a payment-step token (inline flow renders). */
      inlineFinancialAuthority: boolean;
    }
  | { kind: "not-available" };

/** Reads and validates `ref` from the mount-time URL; `null` when absent or malformed. */
function readRecoveryReference(search: string): string | null {
  const reference = new URLSearchParams(search || window.location.search).get("ref");
  return reference !== null && isCredentialRecoveryReference(reference) ? reference : null;
}

export function RegistrationCredentialRecoveryPage(): ReactElement {
  const location = useLocation();
  const auth = useAuth();
  const recoveryClient = useCredentialRecoveryClient();

  // Reference and proof live ONLY in refs: never in DOM-visible state,
  // storage, history, or the URL after the mount-time scrub (A §8.1).
  // `initialReferenceRef` is captured lazily on first render (before the
  // scrub) so a StrictMode simulated unmount/remount can restore the working
  // copy instead of falling through to the no-reference state.
  const initialReferenceRef = useRef<string | null | undefined>(undefined);
  if (initialReferenceRef.current === undefined) {
    initialReferenceRef.current = readRecoveryReference(location.search);
  }
  const referenceRef = useRef<string | null>(null);
  const proofRef = useRef<string | null>(null);
  const bootstrappedRef = useRef(false);
  const mountedRef = useRef(false);
  const financialTokenOwnerRef = useRef<RegistrationFinancialTokenOwner | null>(null);
  // A §8.1: one controller per finalize dispatch; aborted on unmount and on
  // every terminal transition so neither the request nor a pending
  // `Retry-After` re-send can outlive this surface.
  const finalizeAbortRef = useRef<AbortController | null>(null);
  // Pending deferred scrub of `initialReferenceRef` (see the unmount effect).
  const initialReferenceScrubRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flipped only when THIS page's own `refreshSession()` settles. The status
  // resolver below reads `auth.status` no earlier than that render, so a
  // concurrently-running provider bootstrap (`getCurrentSession()` on a mount
  // without `initialSession`) can never be mistaken for the refresh outcome.
  const [refreshSettled, setRefreshSettled] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: "refreshing" });

  const clearSecrets = useCallback((): void => {
    initialReferenceRef.current = null;
    referenceRef.current = null;
    proofRef.current = null;
  }, []);

  const abortFinalize = useCallback((): void => {
    finalizeAbortRef.current?.abort();
    finalizeAbortRef.current = null;
  }, []);

  // Step 3: read `ref` once, scrub the URL before any request, then refresh
  // the session. The refresh runs once per mount; the reference restore and
  // the mounted flag re-run on a StrictMode remount so the cleanup below
  // never strands a valid reference.
  useEffect(() => {
    if (initialReferenceScrubRef.current !== null) {
      clearTimeout(initialReferenceScrubRef.current);
      initialReferenceScrubRef.current = null;
    }
    mountedRef.current = true;
    referenceRef.current = initialReferenceRef.current ?? null;
    if (bootstrappedRef.current) {
      // Revived after the deferred scrub fired: the secret is gone, so a
      // still-pending phase must land on the no-reference terminal.
      if (referenceRef.current === null) {
        setPhase((prev) =>
          prev.kind === "paste" || prev.kind === "refreshing" ? { kind: "no-reference" } : prev,
        );
      }
      return undefined;
    }
    bootstrappedRef.current = true;

    // Scrub on ANY non-bare URL: `?ref=` is the expected carrier, but a
    // fragment (e.g. a pasted `#proof=…` on this path) is bearer-shaped and
    // must not survive in the address bar/history either (clm-web#238 AB3).
    if (
      window.location.pathname !== RECOVERY_PATH ||
      window.location.search !== "" ||
      window.location.hash !== ""
    ) {
      window.history.replaceState(null, "", RECOVERY_PATH);
    }
    if (referenceRef.current === null) {
      setPhase({ kind: "no-reference" });
      return undefined;
    }
    void auth.refreshSession().then(
      () => {
        if (mountedRef.current) {
          setRefreshSettled(true);
        }
      },
      () => {
        if (!mountedRef.current) {
          return;
        }
        clearSecrets();
        setPhase({ kind: "no-reference" });
      },
    );
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, []);

  // Resolve the session committed by THIS page's refresh: authenticated →
  // paste step; otherwise the no-session state (byte-identical to
  // no-reference; no sign-in link).
  useEffect(() => {
    if (!refreshSettled || phase.kind !== "refreshing") {
      return;
    }
    if (auth.status === "authenticated") {
      setPhase({ kind: "paste" });
    } else if (auth.status === "unauthenticated") {
      clearSecrets();
      setPhase({ kind: "no-reference" });
    }
  }, [auth.status, clearSecrets, phase.kind, refreshSettled]);

  // Unmount: drop the working secrets, abort any in-flight finalize (and its
  // pending re-send), and release any financial token this page accepted.
  // `initialReferenceRef` is scrubbed on a deferred 0ms timer so a synchronous
  // StrictMode remount can cancel it (bootstrap effect above) and restore first.
  useEffect(
    () => () => {
      mountedRef.current = false;
      abortFinalize();
      referenceRef.current = null;
      proofRef.current = null;
      clearRegistrationFinancialTokenByOwner(financialTokenOwnerRef.current);
      initialReferenceScrubRef.current = setTimeout(() => {
        initialReferenceScrubRef.current = null;
        initialReferenceRef.current = null;
      }, 0);
    },
    [abortFinalize],
  );

  // Clear proof/reference (and cancel any finalize) on every terminal transition.
  useEffect(() => {
    if (phase.kind !== "refreshing" && phase.kind !== "paste") {
      abortFinalize();
      clearSecrets();
    }
  }, [abortFinalize, clearSecrets, phase.kind]);

  // The async body captures NO proof- or reference-bearing binding: both are
  // read from the synchronously revocable holders only AFTER the
  // un-abortable `getAccessToken()` await and the disposal check, so an
  // unmount during token acquisition (holders nulled above) leaves nothing
  // proof-bearing reachable and nothing to dispatch.
  const dispatchFinalize = useCallback(async (): Promise<void> => {
    abortFinalize();
    const controller = new AbortController();
    finalizeAbortRef.current = controller;
    let result: CredentialRecoveryFinalizeResult;
    try {
      const token = await auth.getAccessToken();
      if (!mountedRef.current || controller.signal.aborted) {
        return;
      }
      if (!token || proofRef.current === null || referenceRef.current === null) {
        setPhase({ kind: "no-reference" });
        return;
      }
      result = await recoveryClient.finalize(
        { proof: proofRef.current, recoveryReference: referenceRef.current },
        token,
        { signal: controller.signal },
      );
    } catch (error) {
      if (!mountedRef.current || controller.signal.aborted) {
        return;
      }
      // 401/403: the bearer no longer authorises this reference → the
      // no-session state. Any other failure (422 rotated key, 429, 503 the
      // client could not recover) → the same generic guidance (L185).
      setPhase(
        error instanceof CredentialRecoveryApiError &&
          (error.status === 401 || error.status === 403)
          ? { kind: "no-reference" }
          : { kind: "not-available" },
      );
      return;
    } finally {
      proofRef.current = null;
      if (finalizeAbortRef.current === controller) {
        finalizeAbortRef.current = null;
      }
    }
    if (!mountedRef.current || controller.signal.aborted) {
      return;
    }
    // The client already validates the full `completed` shape (Copilot
    // 3983925915); the defensive object check here keeps this branch from
    // dereferencing `financialHandoff` should a test double or future client
    // hand over a partial body, falling closed to the generic state instead.
    const completion = result.outcome === "completed" ? result.completion : null;
    if (completion && typeof completion === "object" && completion.financialHandoff) {
      const accepted = acceptCompletionFinancialHandoff(completion.financialHandoff);
      financialTokenOwnerRef.current = accepted.tokenOwner;
      setPhase({
        kind: "completed",
        correlationId: completion.correlationId,
        handoff: accepted.descriptor,
        inlineFinancialAuthority: accepted.tokenOwner !== null,
      });
      return;
    }
    setPhase({ kind: "not-available" });
  }, [abortFinalize, auth, recoveryClient]);

  // Stage the pasted proof in the holder and hand off; this synchronous frame
  // (the only one that ever binds `proof`) is gone before any await runs.
  const finalize = useCallback(
    (proof: string): Promise<void> => {
      if (referenceRef.current === null) {
        setPhase({ kind: "no-reference" });
        return Promise.resolve();
      }
      proofRef.current = proof;
      return dispatchFinalize();
    },
    [dispatchFinalize],
  );

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <PublicHeader />
      <main className="clm-page-gutter clm-section-spacing flex-1">
        <div className="mx-auto max-w-[800px]">
          <RecoveryPanel phase={phase} onProof={finalize} />
        </div>
      </main>
      <AppFooter />
    </div>
  );
}

function RecoveryPanel({
  phase,
  onProof,
}: {
  phase: Phase;
  onProof: (proof: string) => Promise<void>;
}): ReactElement | null {
  switch (phase.kind) {
    case "refreshing":
      return (
        <section aria-busy="true">
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            Finishing your registration
          </h1>
          <p
            role="status"
            className="mt-2 flex items-center justify-center gap-2 text-base text-gray-600"
          >
            <ClmIcon
              name="loading"
              decorative
              className="h-4 w-4 animate-spin text-clm-brand-blue motion-reduce:animate-none"
            />
            Checking your sign-in…
          </p>
        </section>
      );
    case "paste":
      return <PasteStep onProof={onProof} />;
    case "completed":
      return (
        <CompletedPanel
          correlationId={phase.correlationId}
          handoff={phase.handoff}
          inlineFinancialAuthority={phase.inlineFinancialAuthority}
        />
      );
    case "not-available":
      return (
        <TerminalMessage
          heading="We couldn't finish setting up your account"
          message={<GenericGuidance />}
        />
      );
    case "no-reference":
    default:
      return (
        <TerminalMessage heading="Finish setting up your account" message={NO_REFERENCE_COPY} />
      );
  }
}

function TerminalMessage({
  heading,
  message,
}: {
  heading: string;
  message: ReactNode;
}): ReactElement {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <section>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        {heading}
      </h1>
      <p className="mt-2 text-base text-gray-600">{message}</p>
    </section>
  );
}

/**
 * Step 4: strict local parse of the pasted invitation link. Only the exact
 * URL the sender constructed is accepted — same origin, `/register/invitation`,
 * no query string, and a fragment that is exactly `proof=<InvitationProof>`.
 * Every other input yields one generic error with zero network traffic.
 */
function parseInvitationLink(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (
    url.origin !== window.location.origin ||
    url.pathname !== INVITATION_PATH ||
    url.search !== ""
  ) {
    return null;
  }
  return parseInvitationProofFragment(url.hash);
}

function PasteStep({ onProof }: { onProof: (proof: string) => Promise<void> }): ReactElement {
  // A §8.1 storage-free input: the pasted bearer URL is read straight from
  // the DOM on submit and never mirrored into React state. Detaching the
  // control (route change, page teardown, including after a failed parse)
  // wipes the node value in place so a retained node never keeps the URL.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputFieldRef] = useState(() => secretFieldRef(inputRef));
  const [invalid, setInvalid] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    const proof = parseInvitationLink(inputRef.current?.value ?? "");
    if (proof === null) {
      setInvalid(true);
      setShowFallback(true);
      return;
    }
    // Success: the proof moves to memory and the field is emptied in the
    // same tick so the bearer URL does not linger in the DOM.
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setInvalid(false);
    setShowFallback(false);
    setSubmitting(true);
    void onProof(proof).finally(() => {
      setSubmitting(false);
    });
  };

  const describedBy = invalid
    ? "recovery-invitation-link-help recovery-invitation-link-error"
    : "recovery-invitation-link-help";

  return (
    <section>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        Finish setting up your account
      </h1>
      <form className="mt-6" noValidate onSubmit={handleSubmit}>
        <Label htmlFor="recovery-invitation-link">
          {PASTE_LABEL}
          <span aria-hidden="true" className="ml-0.5 text-alert-red">
            *
          </span>
        </Label>
        <p id="recovery-invitation-link-help" className="mt-2 text-sm text-gray-500">
          {PASTE_HELP}
        </p>
        <Input
          ref={inputFieldRef}
          id="recovery-invitation-link"
          name="invitation-link"
          type="url"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          className="mt-2"
          aria-required="true"
          aria-invalid={invalid ? "true" : undefined}
          aria-describedby={describedBy}
          disabled={submitting}
        />
        {invalid ? (
          <p
            id="recovery-invitation-link-error"
            role="alert"
            className="mt-1 text-sm text-alert-red"
          >
            {PASTE_ERROR}
          </p>
        ) : null}
        {showFallback ? (
          <p className="mt-2 text-sm text-gray-500">
            <RewrittenLinkFallback />
          </p>
        ) : null}
        <Button type="submit" className="mt-4 w-full" disabled={submitting}>
          {submitting ? "Continuing…" : "Continue"}
        </Button>
        {/* DES-1 submitting feedback: the label change above is mirrored in a
            polite live region so screen readers hear the in-flight state. */}
        <p role="status" aria-live="polite" className="sr-only">
          {submitting ? "Continuing…" : ""}
        </p>
      </form>
    </section>
  );
}

/**
 * Member Completed row (registration-checkout-ui-contract.md `completed`,
 * 011-21 "Member completion and re-entry"): the recovery finalize already ran
 * inside a live session, so "Sign in" is replaced by "Continue" to the
 * post-login landing. The financial handoff renders through the shared
 * outcome surface; `signInProven` stays false because this page never saw a
 * credential being created.
 */
function CompletedPanel({
  correlationId,
  handoff,
  inlineFinancialAuthority,
}: {
  correlationId: string;
  handoff: FinancialHandoffDescriptor;
  inlineFinancialAuthority: boolean;
}): ReactElement {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <section>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        Registration complete
      </h1>
      <Alert role="status" variant="success" className="mt-4">
        <AlertTitle>Your registration was completed successfully</AlertTitle>
        <AlertDescription>
          {/* Parity with the invitation page's `completionNextActionCopy`: with
              payment-step authority the financial flow renders directly below,
              so pointing at "instructions you receive" would contradict it. */}
          {inlineFinancialAuthority
            ? "Finish setting up your membership below."
            : "Financial setup continues separately; follow the instructions you receive to finish setting up your membership."}
        </AlertDescription>
      </Alert>
      <RegistrationFinancialOutcome handoff={handoff} signInProven={false} />
      <Button asChild className="mt-4 w-full">
        <Link to={getDefaultReturnTo()}>Continue</Link>
      </Button>
      <SupportReference label="Registration reference" correlationId={correlationId} />
    </section>
  );
}
